import { For, Show, createSignal } from "solid-js";

import { CustomFieldsRenderer } from "~/components/CustomFieldsRenderer";
import { EntityAttachmentPicker } from "~/components/EntityAttachmentPicker";
import {
  useActivities,
  useArchiveNote,
  useCompleteTask,
  useContacts,
  useCreateActivity,
  useCreateNote,
  useNotes,
  usePinNote,
  useUpdateActivity,
  useUpdateNote,
  useWorkspaceRoles,
  useWorkspaceUsers,
} from "~/lib/hooks";
import { formatUserName } from "~/lib/types";
import type {
  ActivityCreate,
  ActivityKind,
  ActivityParticipant,
  ActivityRead,
  ActivityUpdate,
  AttachableEntity,
  NoteAttachmentRef,
  NoteRead,
  NoteVisibility,
} from "~/lib/types";

type Props = {
  wsId: () => string;
  entityType: AttachableEntity;
  entityId: () => string;
};

type TimelineItem =
  | { kind: "note"; row: NoteRead; sortDate: string }
  | { kind: "activity"; row: ActivityRead; sortDate: string };

export function EntityTimeline(props: Props) {
  const notes = useNotes(props.wsId, () => ({
    entity_type: props.entityType,
    entity_id: props.entityId(),
  }));
  const activities = useActivities(props.wsId, () => ({
    primary_entity_type: props.entityType,
    primary_entity_id: props.entityId(),
    limit: 100,
  }));

  const createNote = useCreateNote(props.wsId);
  const updateNote = useUpdateNote(props.wsId);
  const pinNote = usePinNote(props.wsId);
  const archiveNote = useArchiveNote(props.wsId);
  const createActivity = useCreateActivity(props.wsId);
  const updateActivity = useUpdateActivity(props.wsId);
  const completeTask = useCompleteTask(props.wsId);

  const [composer, setComposer] = createSignal<"note" | "activity" | null>(null);

  const items = (): TimelineItem[] => {
    const n: TimelineItem[] = (notes.data?.items ?? []).map((r) => ({
      kind: "note",
      row: r,
      sortDate: r.created_at,
    }));
    const a: TimelineItem[] = (activities.data?.items ?? []).map((r) => ({
      kind: "activity",
      row: r,
      sortDate: r.started_at,
    }));
    const all = [...n, ...a].sort((x, y) => (x.sortDate < y.sortDate ? 1 : -1));
    // Pinned notes first.
    const pinned = all.filter((i) => i.kind === "note" && i.row.pinned);
    const rest = all.filter((i) => !(i.kind === "note" && i.row.pinned));
    return [...pinned, ...rest];
  };

  const submitNote = async (input: {
    body: string;
    visibility: NoteVisibility;
    also_attached_to: NoteAttachmentRef[];
    mentions: string[];
    restricted_role_ids: string[];
  }) => {
    if (!input.body.trim()) return;
    await createNote.mutateAsync({
      body: input.body,
      visibility: input.visibility,
      attached_to_entity_type: props.entityType,
      attached_to_entity_id: props.entityId(),
      also_attached_to: input.also_attached_to.length > 0 ? input.also_attached_to : undefined,
      mentions: input.mentions.length > 0 ? input.mentions : undefined,
      restricted_role_ids:
        input.restricted_role_ids.length > 0 ? input.restricted_role_ids : undefined,
    });
    setComposer(null);
  };

  const submitActivity = async (input: ActivityCreate) => {
    await createActivity.mutateAsync({
      ...input,
      primary_entity_type: props.entityType,
      primary_entity_id: props.entityId(),
    });
    setComposer(null);
  };

  return (
    <div class="card bg-base-100 shadow-sm mt-4">
      <div class="card-body">
        <div class="flex items-center justify-between">
          <h3 class="font-semibold">Timeline</h3>
          <div class="flex gap-2">
            <button class="btn btn-sm btn-ghost" onClick={() => setComposer("note")}>
              + Note
            </button>
            <button class="btn btn-sm btn-ghost" onClick={() => setComposer("activity")}>
              + Activity
            </button>
          </div>
        </div>

        <Show when={composer() === "note"}>
          <NoteComposer
            wsKey={props.wsId}
            primary={() => ({
              entity_type: props.entityType,
              entity_id: props.entityId(),
            })}
            onCancel={() => setComposer(null)}
            onSubmit={submitNote}
          />
        </Show>
        <Show when={composer() === "activity"}>
          <ActivityForm
            wsKey={props.wsId}
            mode="create"
            onCancel={() => setComposer(null)}
            onSubmit={submitActivity}
            pending={() => createActivity.isPending}
          />
        </Show>

        <Show when={notes.isLoading || activities.isLoading}>
          <div role="status" aria-live="polite" class="text-center py-6 opacity-60 text-sm">
            Loading…
          </div>
        </Show>
        <Show when={!notes.isLoading && !activities.isLoading && items().length === 0}>
          <div class="text-center py-8 opacity-60 text-sm">
            No notes or activities yet. Use the buttons above to add one.
          </div>
        </Show>

        <ul class="mt-3 flex flex-col gap-3">
          <For each={items()}>
            {(item) => (
              <Show
                when={item.kind === "note"}
                fallback={
                  <ActivityRow
                    wsKey={props.wsId}
                    activity={(item as Extract<TimelineItem, { kind: "activity" }>).row}
                    onComplete={(act) =>
                      completeTask.mutate({ activityId: act.id, version: act.version })
                    }
                    onUpdate={async (act, body) => {
                      await updateActivity.mutateAsync({
                        activityId: act.id,
                        version: act.version,
                        body,
                      });
                    }}
                  />
                }
              >
                <NoteRow
                  note={(item as Extract<TimelineItem, { kind: "note" }>).row}
                  wsKey={props.wsId}
                  primary={() => ({
                    entity_type: props.entityType,
                    entity_id: props.entityId(),
                  })}
                  onTogglePin={(n) =>
                    pinNote.mutate({ noteId: n.id, pinned: !n.pinned, version: n.version })
                  }
                  onArchive={(n) => archiveNote.mutate({ noteId: n.id, version: n.version })}
                  onUpdate={async (n, body) => {
                    await updateNote.mutateAsync({
                      noteId: n.id,
                      version: n.version,
                      body,
                    });
                  }}
                />
              </Show>
            )}
          </For>
        </ul>
      </div>
    </div>
  );
}

function NoteComposer(props: {
  wsKey: () => string;
  primary: () => NoteAttachmentRef;
  onCancel: () => void;
  onSubmit: (input: {
    body: string;
    visibility: NoteVisibility;
    also_attached_to: NoteAttachmentRef[];
    mentions: string[];
    restricted_role_ids: string[];
  }) => void;
}) {
  const [body, setBody] = createSignal("");
  const [visibility, setVisibility] = createSignal<NoteVisibility>("workspace");
  const [alsoAttached, setAlsoAttached] = createSignal<NoteAttachmentRef[]>([]);
  const [mentions, setMentions] = createSignal<MentionRef[]>([]);
  const [restrictedRoles, setRestrictedRoles] = createSignal<string[]>([]);
  const roles = useWorkspaceRoles(props.wsKey);
  return (
    <div class="mt-3 p-3 bg-base-200 rounded-lg flex flex-col gap-3">
      <textarea
        class="textarea textarea-bordered"
        rows="3"
        placeholder="Write a note…"
        aria-label="Note body"
        value={body()}
        onInput={(e) => setBody(e.currentTarget.value)}
      />
      <div>
        <label class="label py-1">
          <span class="label-text text-xs opacity-70">Mention users (optional)</span>
        </label>
        <MentionPicker wsKey={props.wsKey} value={mentions} onChange={setMentions} />
      </div>
      <div>
        <label class="label py-1">
          <span class="label-text text-xs opacity-70">Also attach to (optional)</span>
        </label>
        <EntityAttachmentPicker
          wsKey={props.wsKey}
          primary={props.primary}
          value={alsoAttached}
          onChange={setAlsoAttached}
        />
      </div>
      <div class="flex items-center gap-2 justify-between">
        <label class="flex items-center gap-2 text-sm">
          <span class="opacity-70">Visibility:</span>
          <select
            class="select select-bordered select-sm"
            aria-label="Note visibility"
            value={visibility()}
            onChange={(e) => setVisibility(e.currentTarget.value as NoteVisibility)}
          >
            <option value="workspace">Workspace</option>
            <option value="private">Private (only me)</option>
            <option value="restricted">Restricted (pick roles)</option>
          </select>
        </label>
        <div class="flex gap-2">
          <button class="btn btn-sm btn-ghost" onClick={props.onCancel}>
            Cancel
          </button>
          <button
            class="btn btn-sm btn-primary"
            disabled={
              !body().trim() || (visibility() === "restricted" && restrictedRoles().length === 0)
            }
            onClick={() =>
              props.onSubmit({
                body: body(),
                visibility: visibility(),
                also_attached_to: alsoAttached(),
                mentions: mentions().map((m) => m.id),
                restricted_role_ids: visibility() === "restricted" ? restrictedRoles() : [],
              })
            }
          >
            Save note
          </button>
        </div>
      </div>
      <Show when={visibility() === "restricted"}>
        <div>
          <label class="label py-1">
            <span class="label-text text-xs opacity-70">Visible to roles (pick at least one)</span>
          </label>
          <div class="flex flex-wrap gap-2">
            <For each={roles.data ?? []}>
              {(r) => (
                <label class="cursor-pointer flex items-center gap-2 px-2 py-1 rounded-md border border-base-300">
                  <input
                    type="checkbox"
                    class="checkbox checkbox-xs"
                    checked={restrictedRoles().includes(r.id)}
                    onChange={(e) => {
                      const next = new Set(restrictedRoles());
                      if (e.currentTarget.checked) next.add(r.id);
                      else next.delete(r.id);
                      setRestrictedRoles(Array.from(next));
                    }}
                  />
                  <span class="text-sm">{r.name}</span>
                  <span class="text-xs opacity-60">({r.level})</span>
                </label>
              )}
            </For>
          </div>
        </div>
      </Show>
    </div>
  );
}

function NoteRow(props: {
  note: NoteRead;
  wsKey: () => string;
  primary: () => NoteAttachmentRef;
  onTogglePin: (n: NoteRead) => void;
  onArchive: (n: NoteRead) => void;
  onUpdate: (
    n: NoteRead,
    body: {
      body?: string;
      visibility?: NoteVisibility;
      also_attached_to?: NoteAttachmentRef[];
    },
  ) => Promise<void>;
}) {
  const [editing, setEditing] = createSignal(false);
  const [draftBody, setDraftBody] = createSignal("");
  const [draftVisibility, setDraftVisibility] = createSignal<NoteVisibility>("workspace");
  const [draftAlsoAttached, setDraftAlsoAttached] = createSignal<NoteAttachmentRef[]>([]);
  const [busy, setBusy] = createSignal(false);
  const [editError, setEditError] = createSignal<string | null>(null);

  const startEdit = () => {
    setDraftBody(props.note.body);
    setDraftVisibility(props.note.visibility as NoteVisibility);
    setDraftAlsoAttached([...(props.note.also_attached_to ?? [])]);
    setEditError(null);
    setEditing(true);
  };

  // Compare two attachment lists by (entity_type, entity_id), order-independent.
  const sameAttachments = (a: NoteAttachmentRef[], b: NoteAttachmentRef[]): boolean => {
    if (a.length !== b.length) return false;
    const key = (r: NoteAttachmentRef) => `${r.entity_type}:${r.entity_id}`;
    const aSet = new Set(a.map(key));
    return b.every((r) => aSet.has(key(r)));
  };

  const save = async () => {
    if (!draftBody().trim()) return;
    setBusy(true);
    setEditError(null);
    try {
      const body: {
        body?: string;
        visibility?: NoteVisibility;
        also_attached_to?: NoteAttachmentRef[];
      } = {};
      if (draftBody() !== props.note.body) body.body = draftBody();
      if (draftVisibility() !== props.note.visibility) body.visibility = draftVisibility();
      if (!sameAttachments(draftAlsoAttached(), props.note.also_attached_to ?? [])) {
        body.also_attached_to = draftAlsoAttached();
      }
      if (Object.keys(body).length > 0) await props.onUpdate(props.note, body);
      setEditing(false);
    } catch (err) {
      setEditError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li class="flex gap-3 border-l-4 border-info pl-3 py-2">
      <div class="flex-1">
        <div class="flex items-center gap-2 text-xs opacity-60">
          <span class="badge badge-info badge-sm">Note</span>
          <Show when={props.note.pinned}>
            <span class="badge badge-warning badge-sm">📌 pinned</span>
          </Show>
          <Show when={props.note.visibility !== "workspace"}>
            <span class="badge badge-ghost badge-sm">{props.note.visibility}</span>
          </Show>
          <span>{new Date(props.note.created_at).toLocaleString()}</span>
        </div>
        <Show
          when={editing()}
          fallback={
            <>
              <p class="mt-1 whitespace-pre-wrap">{props.note.body}</p>
              <Show when={(props.note.mentions?.length ?? 0) > 0}>
                <div class="flex flex-wrap gap-1 mt-2">
                  <For each={props.note.mentions ?? []}>
                    {(m) => (
                      <span class="badge badge-secondary badge-outline badge-sm">
                        @{m.slice(0, 14)}
                      </span>
                    )}
                  </For>
                </div>
              </Show>
              <Show when={(props.note.also_attached_to?.length ?? 0) > 0}>
                <div class="flex flex-wrap gap-1 mt-2">
                  <For each={props.note.also_attached_to ?? []}>
                    {(ref) => (
                      <span class="badge badge-ghost badge-sm">
                        <span class="opacity-60 mr-1">also:</span>
                        {ref.entity_type}
                      </span>
                    )}
                  </For>
                </div>
              </Show>
            </>
          }
        >
          <div class="mt-2 flex flex-col gap-2">
            <textarea
              class="textarea textarea-bordered"
              rows="3"
              aria-label="Edit note body"
              value={draftBody()}
              onInput={(e) => setDraftBody(e.currentTarget.value)}
            />
            <div>
              <label class="label py-0">
                <span class="label-text text-xs opacity-70">Also attach to</span>
              </label>
              <EntityAttachmentPicker
                wsKey={props.wsKey}
                primary={props.primary}
                value={draftAlsoAttached}
                onChange={setDraftAlsoAttached}
              />
            </div>
            <div class="flex items-center justify-between gap-2">
              <label class="flex items-center gap-2 text-sm">
                <span class="opacity-70">Visibility:</span>
                <select
                  class="select select-bordered select-xs"
                  aria-label="Note visibility"
                  value={draftVisibility()}
                  onChange={(e) => setDraftVisibility(e.currentTarget.value as NoteVisibility)}
                >
                  <option value="workspace">Workspace</option>
                  <option value="private">Private</option>
                </select>
              </label>
              <div class="flex gap-1">
                <button
                  class="btn btn-ghost btn-xs"
                  onClick={() => setEditing(false)}
                  disabled={busy()}
                >
                  Cancel
                </button>
                <button
                  class="btn btn-primary btn-xs"
                  disabled={busy() || !draftBody().trim()}
                  onClick={save}
                >
                  {busy() ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
            <Show when={editError()}>
              <div class="text-error text-xs" role="alert">
                {editError()}
              </div>
            </Show>
          </div>
        </Show>
      </div>
      <Show when={!editing()}>
        <div class="flex flex-col gap-1">
          <button
            class="btn btn-ghost btn-xs"
            title={props.note.pinned ? "Unpin" : "Pin"}
            onClick={() => props.onTogglePin(props.note)}
          >
            {props.note.pinned ? "Unpin" : "Pin"}
          </button>
          <button class="btn btn-ghost btn-xs" onClick={startEdit}>
            Edit
          </button>
          <button
            class="btn btn-ghost btn-xs text-error"
            onClick={() => props.onArchive(props.note)}
          >
            Archive
          </button>
        </div>
      </Show>
    </li>
  );
}

function ActivityRow(props: {
  activity: ActivityRead;
  wsKey: () => string;
  onComplete: (a: ActivityRead) => void;
  onUpdate: (a: ActivityRead, body: ActivityUpdate) => Promise<void>;
}) {
  const kindColor: Record<ActivityKind, string> = {
    meeting: "badge-success",
    call: "badge-warning",
    email: "badge-info",
    task: "badge-secondary",
    system_event: "badge-neutral",
  };
  const [editing, setEditing] = createSignal(false);

  return (
    <li class="flex gap-3 border-l-4 border-success pl-3 py-2">
      <div class="flex-1">
        <div class="flex items-center gap-2 text-xs opacity-60">
          <span class={`badge ${kindColor[props.activity.kind]} badge-sm`}>
            {props.activity.kind}
          </span>
          <Show when={props.activity.direction}>
            <span class="badge badge-outline badge-sm">{props.activity.direction}</span>
          </Show>
          <Show when={props.activity.status !== "completed"}>
            <span class="badge badge-outline badge-sm">{props.activity.status}</span>
          </Show>
          <span>{new Date(props.activity.started_at).toLocaleString()}</span>
          <Show when={props.activity.duration_seconds}>
            <span>· {Math.round(props.activity.duration_seconds! / 60)} min</span>
          </Show>
        </div>
        <Show
          when={editing()}
          fallback={
            <>
              <p class="mt-1 font-medium">{props.activity.subject}</p>
              <Show when={props.activity.body}>
                <p class="text-sm opacity-80 whitespace-pre-wrap mt-1">{props.activity.body}</p>
              </Show>
              <Show when={props.activity.outcome}>
                <p class="text-xs opacity-60 mt-1">Outcome: {props.activity.outcome}</p>
              </Show>
              <Show when={(props.activity.participants?.length ?? 0) > 0}>
                <div class="flex flex-wrap gap-1 mt-2">
                  <For each={props.activity.participants ?? []}>
                    {(p) => (
                      <span class="badge badge-ghost badge-sm">
                        <span class="opacity-60 mr-1">{p.kind}:</span>
                        {p.id.slice(0, 14)}
                      </span>
                    )}
                  </For>
                </div>
              </Show>
              <Show when={props.activity.due_date}>
                <p class="text-xs opacity-60 mt-1">
                  Due {new Date(props.activity.due_date!).toLocaleString()}
                </p>
              </Show>
            </>
          }
        >
          <div class="mt-2">
            <ActivityForm
              wsKey={props.wsKey}
              mode="edit"
              initial={props.activity}
              onCancel={() => setEditing(false)}
              onSubmit={async (body) => {
                await props.onUpdate(props.activity, body);
                setEditing(false);
              }}
            />
          </div>
        </Show>
      </div>
      <Show when={!editing()}>
        <div class="flex flex-col gap-1">
          <Show when={props.activity.kind === "task" && props.activity.status !== "completed"}>
            <button
              class="btn btn-ghost btn-xs text-success"
              onClick={() => props.onComplete(props.activity)}
            >
              ✓ Complete
            </button>
          </Show>
          <button class="btn btn-ghost btn-xs" onClick={() => setEditing(true)}>
            Edit
          </button>
        </div>
      </Show>
    </li>
  );
}

// Per-kind activity form used for both create + edit. Re-renders the
// relevant subset of fields when `kind` changes so the user sees what
// matters for the activity type.
function ActivityForm(props: {
  wsKey: () => string;
  mode: "create" | "edit";
  initial?: ActivityRead;
  onCancel: () => void;
  // The body shape differs by mode but they overlap; cast at call site.
  onSubmit: (body: ActivityCreate & ActivityUpdate) => Promise<void> | void;
  pending?: () => boolean;
}) {
  const initial = props.initial;
  const [kind, setKind] = createSignal<ActivityKind>((initial?.kind as ActivityKind) ?? "meeting");
  const [subject, setSubject] = createSignal(initial?.subject ?? "");
  const [body, setBody] = createSignal(initial?.body ?? "");
  const [direction, setDirection] = createSignal<"" | "inbound" | "outbound">(
    (initial?.direction as "" | "inbound" | "outbound" | null | undefined) ?? "",
  );
  const [durationMin, setDurationMin] = createSignal<string>(
    initial?.duration_seconds ? String(Math.round(initial.duration_seconds / 60)) : "",
  );
  const [outcome, setOutcome] = createSignal(initial?.outcome ?? "");
  // datetime-local needs `YYYY-MM-DDTHH:mm` (no zone). Convert if available.
  const toLocalInput = (iso: string | null | undefined): string => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [startedAt, setStartedAt] = createSignal(toLocalInput(initial?.started_at));
  const [dueDate, setDueDate] = createSignal(toLocalInput(initial?.due_date));
  const [participants, setParticipants] = createSignal<ActivityParticipant[]>(
    initial?.participants ?? [],
  );
  const [customFields, setCustomFields] = createSignal<Record<string, unknown>>(
    (initial?.custom_fields as Record<string, unknown>) ?? {},
  );
  const [busy, setBusy] = createSignal(false);
  const [err, setErr] = createSignal<string | null>(null);

  const showField = (k: ActivityKind, field: string): boolean => {
    if (field === "subject") return true;
    if (field === "body") return k !== "task" ? true : true; // body shown for all
    if (field === "direction") return k === "call" || k === "email";
    if (field === "duration") return k === "meeting" || k === "call";
    if (field === "started_at") return k === "meeting" || k === "call";
    if (field === "outcome") return k === "meeting" || k === "call";
    if (field === "due_date") return k === "task";
    if (field === "participants") return k === "meeting" || k === "call" || k === "email";
    return false;
  };

  const submit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!subject().trim()) return;
    setBusy(true);
    setErr(null);
    const k = kind();
    const payload: ActivityCreate & ActivityUpdate = {
      // Create-required fields (ignored in edit-mode payload via PATCH validation):
      kind: k,
      subject: subject().trim(),
      // Optional fields by kind:
      body: body().trim() ? body() : undefined,
      direction:
        showField(k, "direction") && direction()
          ? (direction() as "inbound" | "outbound")
          : undefined,
      duration_seconds:
        showField(k, "duration") && durationMin()
          ? Math.max(0, Math.round(Number(durationMin()) * 60))
          : undefined,
      started_at:
        showField(k, "started_at") && startedAt() ? new Date(startedAt()).toISOString() : undefined,
      outcome: showField(k, "outcome") && outcome().trim() ? outcome().trim() : undefined,
      due_date:
        showField(k, "due_date") && dueDate() ? new Date(dueDate()).toISOString() : undefined,
      participants:
        showField(k, "participants") && participants().length > 0 ? participants() : undefined,
      custom_fields: Object.keys(customFields()).length > 0 ? customFields() : undefined,
      // Sensible defaults: meetings/calls/emails default to "completed"
      // (i.e. logged after the fact); tasks default to "planned".
      status: k === "task" ? "planned" : "completed",
      // primary_entity_* are injected by the caller for create; ignored on edit.
      primary_entity_type: undefined as never,
      primary_entity_id: undefined as never,
    };
    // Strip the create-only required-but-undefined fields when editing so
    // the PATCH payload doesn't carry junk.
    if (props.mode === "edit") {
      const {
        kind: _k,
        primary_entity_type: _pet,
        primary_entity_id: _pei,
        ...rest
      } = payload as unknown as Record<string, unknown>;
      try {
        await props.onSubmit(rest as ActivityCreate & ActivityUpdate);
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      await props.onSubmit(payload);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      aria-label={props.mode === "create" ? "Log activity" : "Edit activity"}
      class="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-base-200 rounded-lg"
    >
      <Show when={props.mode === "create"}>
        <select
          class="select select-bordered select-sm md:col-span-2"
          aria-label="Activity kind"
          value={kind()}
          onChange={(e) => setKind(e.currentTarget.value as ActivityKind)}
        >
          <option value="meeting">Meeting</option>
          <option value="call">Call</option>
          <option value="email">Email (logged)</option>
          <option value="task">Task</option>
        </select>
      </Show>
      <input
        class="input input-bordered input-sm md:col-span-2"
        placeholder="Subject"
        aria-label="Subject"
        required
        value={subject()}
        onInput={(e) => setSubject(e.currentTarget.value)}
      />

      <Show when={showField(kind(), "direction")}>
        <select
          class="select select-bordered select-sm"
          aria-label="Direction"
          value={direction()}
          onChange={(e) => setDirection(e.currentTarget.value as "" | "inbound" | "outbound")}
        >
          <option value="">Direction…</option>
          <option value="inbound">Inbound</option>
          <option value="outbound">Outbound</option>
        </select>
      </Show>

      <Show when={showField(kind(), "started_at")}>
        <input
          type="datetime-local"
          class="input input-bordered input-sm"
          aria-label="When"
          value={startedAt()}
          onInput={(e) => setStartedAt(e.currentTarget.value)}
        />
      </Show>

      <Show when={showField(kind(), "duration")}>
        <input
          type="number"
          min="0"
          step="1"
          class="input input-bordered input-sm"
          placeholder="Duration (minutes)"
          aria-label="Duration in minutes"
          value={durationMin()}
          onInput={(e) => setDurationMin(e.currentTarget.value)}
        />
      </Show>

      <Show when={showField(kind(), "due_date")}>
        <input
          type="datetime-local"
          class="input input-bordered input-sm md:col-span-2"
          aria-label="Due"
          value={dueDate()}
          onInput={(e) => setDueDate(e.currentTarget.value)}
        />
      </Show>

      <Show when={showField(kind(), "outcome")}>
        <input
          class="input input-bordered input-sm md:col-span-2"
          placeholder="Outcome (e.g. interested, no-show)"
          aria-label="Outcome"
          maxLength={64}
          value={outcome()}
          onInput={(e) => setOutcome(e.currentTarget.value)}
        />
      </Show>

      <Show when={showField(kind(), "participants")}>
        <div class="md:col-span-2">
          <label class="label py-1">
            <span class="label-text text-xs opacity-70">Participants (optional)</span>
          </label>
          <ParticipantsPicker wsKey={props.wsKey} value={participants} onChange={setParticipants} />
        </div>
      </Show>

      <textarea
        class="textarea textarea-bordered md:col-span-2"
        rows="3"
        placeholder="Notes (optional)"
        aria-label="Notes"
        value={body()}
        onInput={(e) => setBody(e.currentTarget.value)}
      />

      <div class="md:col-span-2">
        <CustomFieldsRenderer
          wsKey={props.wsKey}
          entityType="activity"
          values={customFields}
          onChange={(key, value) =>
            setCustomFields((cur) => {
              const next = { ...cur };
              if (value === null || value === "" || (Array.isArray(value) && value.length === 0)) {
                delete next[key];
              } else {
                next[key] = value;
              }
              return next;
            })
          }
        />
      </div>

      <Show when={err()}>
        <div class="md:col-span-2 text-error text-xs" role="alert">
          {err()}
        </div>
      </Show>

      <div class="md:col-span-2 flex gap-2 justify-end">
        <button
          type="button"
          class="btn btn-sm btn-ghost"
          onClick={props.onCancel}
          disabled={busy()}
        >
          Cancel
        </button>
        <button type="submit" class="btn btn-sm btn-primary" disabled={busy() || props.pending?.()}>
          {busy() || props.pending?.()
            ? "Saving…"
            : props.mode === "create"
              ? "Log activity"
              : "Save"}
        </button>
      </div>
    </form>
  );
}

// Mention picker — used by the note composer to select users to @mention.
// Stored as a list of {id, name} chips; only the ids are sent to the API
// (the name is for rendering in the chip until the user hits save).
type MentionRef = { id: string; name: string };

function MentionPicker(props: {
  wsKey: () => string;
  value: () => MentionRef[];
  onChange: (next: MentionRef[]) => void;
}) {
  const [query, setQuery] = createSignal("");
  const users = useWorkspaceUsers(props.wsKey, query);

  const has = (id: string) => props.value().some((m) => m.id === id);
  const add = (id: string, name: string) => {
    if (has(id)) return;
    props.onChange([...props.value(), { id, name }]);
    setQuery("");
  };
  const remove = (id: string) => {
    props.onChange(props.value().filter((m) => m.id !== id));
  };

  return (
    <div>
      <Show when={props.value().length > 0}>
        <div class="flex flex-wrap gap-1 mb-2">
          <For each={props.value()}>
            {(m) => (
              <span class="badge badge-secondary badge-outline gap-1 pl-2">
                @{m.name}
                <button
                  type="button"
                  class="btn btn-ghost btn-xs btn-circle"
                  aria-label={`Remove mention of ${m.name}`}
                  onClick={() => remove(m.id)}
                >
                  ✕
                </button>
              </span>
            )}
          </For>
        </div>
      </Show>
      <input
        type="search"
        class="input input-bordered input-sm w-full"
        placeholder="Search users to mention…"
        aria-label="Search users to mention"
        value={query()}
        onInput={(e) => setQuery(e.currentTarget.value)}
      />
      <Show when={query().length > 0}>
        <ul class="mt-1 max-h-40 overflow-y-auto divide-y divide-base-300 bg-base-100 rounded-lg border border-base-300">
          <For each={users.data?.items ?? []}>
            {(u) => (
              <li>
                <button
                  type="button"
                  class="w-full text-left px-3 py-2 hover:bg-base-200 disabled:opacity-60"
                  disabled={has(u.id)}
                  onClick={() => add(u.id, formatUserName(u))}
                >
                  <span class="font-medium">{formatUserName(u)}</span>
                  <span class="text-xs opacity-60 ml-2">{u.email}</span>
                  <Show when={has(u.id)}>
                    <span class="badge badge-ghost badge-xs ml-2">already mentioned</span>
                  </Show>
                </button>
              </li>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
}

// ParticipantsPicker — search-as-you-type across contacts and users.
// Stored as `[{kind:'contact'|'user', id}]` to match the backend JSON shape.
// A tabbed selector switches the search scope; chips render above the input
// and are click-removable.
function ParticipantsPicker(props: {
  wsKey: () => string;
  value: () => { kind: "contact" | "user"; id: string }[];
  onChange: (next: { kind: "contact" | "user"; id: string }[]) => void;
}) {
  const [scope, setScope] = createSignal<"contact" | "user">("contact");
  const [query, setQuery] = createSignal("");

  const contactResults = useContacts(props.wsKey, () => ({
    q: scope() === "contact" ? query() || undefined : undefined,
    limit: scope() === "contact" ? 8 : 1,
  }));
  const userResults = useWorkspaceUsers(props.wsKey, () => (scope() === "user" ? query() : ""));

  const value = props.value;
  const isPicked = (kind: "contact" | "user", id: string) =>
    value().some((p) => p.kind === kind && p.id === id);

  const add = (kind: "contact" | "user", id: string) => {
    if (isPicked(kind, id)) return;
    props.onChange([...value(), { kind, id }]);
    setQuery("");
  };

  const remove = (kind: "contact" | "user", id: string) => {
    props.onChange(value().filter((p) => !(p.kind === kind && p.id === id)));
  };

  // Display name lookup for the chip: best-effort, falls back to truncated id.
  const labelFor = (kind: "contact" | "user", id: string): string => {
    if (kind === "contact") {
      const m = contactResults.data?.items.find((x) => x.id === id);
      return m ? (m.display_name ?? m.full_name ?? id.slice(0, 14)) : id.slice(0, 14);
    }
    const m = userResults.data?.items.find((x) => x.id === id);
    return m ? formatUserName(m) : id.slice(0, 14);
  };

  return (
    <div>
      <Show when={value().length > 0}>
        <div class="flex flex-wrap gap-1 mb-2">
          <For each={value()}>
            {(p) => (
              <span class="badge badge-outline gap-1 pl-2">
                <span class="opacity-60">{p.kind}:</span>
                {labelFor(p.kind, p.id)}
                <button
                  type="button"
                  class="btn btn-ghost btn-xs btn-circle"
                  aria-label={`Remove participant ${p.id}`}
                  onClick={() => remove(p.kind, p.id)}
                >
                  ✕
                </button>
              </span>
            )}
          </For>
        </div>
      </Show>
      <div class="flex gap-2">
        <div class="join" role="group" aria-label="Participant scope">
          <button
            type="button"
            class={`btn btn-xs join-item ${scope() === "contact" ? "btn-active" : ""}`}
            aria-pressed={scope() === "contact"}
            onClick={() => {
              setScope("contact");
              setQuery("");
            }}
          >
            Contacts
          </button>
          <button
            type="button"
            class={`btn btn-xs join-item ${scope() === "user" ? "btn-active" : ""}`}
            aria-pressed={scope() === "user"}
            onClick={() => {
              setScope("user");
              setQuery("");
            }}
          >
            Users
          </button>
        </div>
        <input
          type="search"
          class="input input-bordered input-sm flex-1"
          placeholder={`Search ${scope() === "contact" ? "contacts" : "users"}…`}
          aria-label={`Search ${scope()}s`}
          value={query()}
          onInput={(e) => setQuery(e.currentTarget.value)}
        />
      </div>
      <Show when={query().length > 0}>
        <ul class="mt-2 max-h-40 overflow-y-auto divide-y divide-base-300 bg-base-100 rounded-lg border border-base-300">
          <Show when={scope() === "contact"}>
            <For each={contactResults.data?.items ?? []}>
              {(c) => (
                <li>
                  <button
                    type="button"
                    class="w-full text-left px-3 py-2 hover:bg-base-200 disabled:opacity-60"
                    disabled={isPicked("contact", c.id)}
                    onClick={() => add("contact", c.id)}
                  >
                    <span class="font-medium">{c.display_name ?? c.full_name ?? "Unnamed"}</span>
                    <Show when={c.primary_email}>
                      <span class="text-xs opacity-60 ml-2">{c.primary_email}</span>
                    </Show>
                    <Show when={isPicked("contact", c.id)}>
                      <span class="badge badge-ghost badge-xs ml-2">already added</span>
                    </Show>
                  </button>
                </li>
              )}
            </For>
          </Show>
          <Show when={scope() === "user"}>
            <For each={userResults.data?.items ?? []}>
              {(u) => (
                <li>
                  <button
                    type="button"
                    class="w-full text-left px-3 py-2 hover:bg-base-200 disabled:opacity-60"
                    disabled={isPicked("user", u.id)}
                    onClick={() => add("user", u.id)}
                  >
                    <span class="font-medium">{formatUserName(u)}</span>
                    <span class="text-xs opacity-60 ml-2">{u.email}</span>
                    <Show when={isPicked("user", u.id)}>
                      <span class="badge badge-ghost badge-xs ml-2">already added</span>
                    </Show>
                  </button>
                </li>
              )}
            </For>
          </Show>
        </ul>
      </Show>
    </div>
  );
}
